import React, { useState } from 'react';
import { LogOut, Hammer, Shield, HardHat, Eye, EyeOff, Wifi, WifiOff, AlertCircle, Wallet } from 'lucide-react';
import AdminDashboard from './pages/AdminDashboard';
import EngineerDashboard from './pages/EngineerDashboard';
import FinanceDashboard from './pages/FinanceDashboard';
import { api } from './api';

const LOGIN_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: 'Inter', sans-serif; background: #f0f4ff; -webkit-font-smoothing: antialiased; }

  .login-root {
    min-height: 100vh;
    display: flex;
    background: #f0f4ff;
  }

  /* Left panel — visible on wide screens */
  .login-left {
    display: none;
    width: 44%;
    background: linear-gradient(155deg, #1e3a8a 0%, #1d4ed8 50%, #2563eb 100%);
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 56px 48px;
    position: relative;
    overflow: hidden;
  }
  @media (min-width: 900px) { .login-left { display: flex; } }

  .login-left::before {
    content: '';
    position: absolute;
    width: 600px; height: 600px;
    border-radius: 50%;
    border: 1px solid rgba(255,255,255,0.08);
    top: -180px; right: -180px;
  }
  .login-left::after {
    content: '';
    position: absolute;
    width: 400px; height: 400px;
    border-radius: 50%;
    border: 1px solid rgba(255,255,255,0.06);
    bottom: -120px; left: -120px;
  }

  .login-left-content { position: relative; z-index: 2; text-align: center; }
  .login-left-icon {
    width: 76px; height: 76px;
    background: rgba(255,255,255,0.12);
    border-radius: 20px;
    display: inline-flex; align-items: center; justify-content: center;
    margin-bottom: 28px;
    border: 1px solid rgba(255,255,255,0.18);
  }
  .login-left-title {
    font-size: 28px; font-weight: 800; color: #fff;
    line-height: 1.25; margin-bottom: 12px; letter-spacing: -0.5px;
  }
  .login-left-sub {
    font-size: 14px; color: rgba(255,255,255,0.65); line-height: 1.65;
  }
  .login-left-pills {
    display: flex; gap: 10px; margin-top: 40px; flex-wrap: wrap; justify-content: center;
  }
  .login-pill {
    background: rgba(255,255,255,0.10);
    border: 1px solid rgba(255,255,255,0.15);
    border-radius: 8px; padding: 10px 18px;
    font-size: 12px; color: rgba(255,255,255,0.80); font-weight: 600;
  }

  /* Right form */
  .login-right {
    flex: 1;
    display: flex; align-items: center; justify-content: center;
    padding: 32px 24px;
  }
  .login-panel {
    width: 100%; max-width: 420px;
    animation: panelIn 0.45s cubic-bezier(0.16,1,0.3,1) both;
  }
  @keyframes panelIn {
    from { opacity: 0; transform: translateY(18px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .login-brand { margin-bottom: 28px; }
  .login-brand-icon {
    width: 44px; height: 44px;
    background: linear-gradient(135deg, #1d4ed8, #3b82f6);
    border-radius: 11px;
    display: inline-flex; align-items: center; justify-content: center;
    margin-bottom: 14px;
    box-shadow: 0 4px 14px rgba(29,78,216,0.30);
  }
  .login-brand-name {
    display: block; font-size: 22px; font-weight: 800;
    color: #0f172a; letter-spacing: -0.4px;
  }
  .login-brand-sub { display: block; font-size: 13px; color: #64748b; margin-top: 3px; }

  .login-card {
    background: #fff;
    border: 1px solid #e2e8f0;
    border-radius: 16px;
    padding: 30px 28px;
    box-shadow: 0 4px 20px rgba(15,23,42,0.07), 0 1px 4px rgba(15,23,42,0.04);
  }
  .login-card-title { font-size: 17px; font-weight: 700; color: #0f172a; margin-bottom: 4px; }
  .login-card-sub   { font-size: 13px; color: #64748b; margin-bottom: 22px; }

  .login-field { margin-bottom: 14px; }
  .login-field label { display: block; font-size: 12.5px; font-weight: 600; color: #374151; margin-bottom: 5px; }
  .login-input-wrap { position: relative; }
  .login-input-wrap .fi {
    position: absolute; left: 12px; top: 50%; transform: translateY(-50%);
    color: #9ca3af; pointer-events: none; display: flex;
  }
  .login-input-wrap .eye-btn {
    position: absolute; right: 12px; top: 50%; transform: translateY(-50%);
    background: none; border: none; cursor: pointer;
    color: #9ca3af; padding: 0; display: flex; align-items: center;
  }
  .login-input-wrap .eye-btn:hover { color: #1d4ed8; }
  .login-input {
    width: 100%; padding: 10px 40px;
    background: #f8fafc; border: 1.5px solid #e2e8f0;
    border-radius: 8px; color: #0f172a; font-size: 14px;
    font-family: 'Inter', sans-serif; outline: none;
    transition: border-color 0.15s, box-shadow 0.15s, background 0.15s;
  }
  .login-input::placeholder { color: #c4cdd8; }
  .login-input:focus { background: #fff; border-color: #1d4ed8; box-shadow: 0 0 0 3px rgba(29,78,216,0.10); }

  .login-error {
    display: flex; align-items: flex-start; gap: 8px;
    background: #fef2f2; border: 1px solid #fecaca;
    color: #dc2626; padding: 10px 13px;
    border-radius: 8px; font-size: 13px; margin-bottom: 14px; font-weight: 500;
  }

  .login-btn {
    width: 100%; padding: 11px;
    background: linear-gradient(135deg, #1d4ed8, #2563eb);
    color: #fff; border: none; border-radius: 8px;
    font-size: 14px; font-weight: 700;
    font-family: 'Inter', sans-serif; cursor: pointer;
    transition: all 0.15s; margin-top: 6px;
    display: flex; align-items: center; justify-content: center; gap: 8px;
    box-shadow: 0 2px 8px rgba(29,78,216,0.28);
    letter-spacing: 0.1px;
  }
  .login-btn:hover:not(:disabled) { background: linear-gradient(135deg, #1e40af, #1d4ed8); transform: translateY(-1px); box-shadow: 0 4px 14px rgba(29,78,216,0.38); }
  .login-btn:active:not(:disabled) { transform: scale(0.99); }
  .login-btn:disabled { opacity: 0.55; cursor: not-allowed; }

  @keyframes spin { to { transform: rotate(360deg); } }
  .spin { animation: spin 0.75s linear infinite; display: inline-block; }

  /* App shell */
  .app-root { display: flex; flex-direction: column; height: 100vh; overflow: hidden; }

  .top-bar {
    height: 54px; background: #fff;
    border-bottom: 1px solid #e2e8f0;
    display: flex; align-items: center;
    padding: 0 24px; gap: 14px;
    flex-shrink: 0; z-index: 50;
    box-shadow: 0 1px 3px rgba(15,23,42,0.05);
  }
  .top-bar-logo {
    font-family: 'Inter', sans-serif; font-size: 14px; font-weight: 800;
    color: #0f172a; letter-spacing: -0.2px;
    display: flex; align-items: center; gap: 8px;
  }
  .top-bar-logo-icon {
    width: 30px; height: 30px;
    background: linear-gradient(135deg, #1d4ed8, #3b82f6);
    border-radius: 7px; display: flex; align-items: center; justify-content: center;
  }
  .top-bar-sep { flex: 1; }
  .top-bar-role {
    display: flex; align-items: center; gap: 5px;
    padding: 4px 10px; border-radius: 6px;
    font-size: 11px; font-weight: 700;
    text-transform: uppercase; letter-spacing: 0.5px; border: 1px solid;
  }
  .role-admin    { background: #eff6ff; color: #1d4ed8; border-color: #bfdbfe; }
  .role-engineer { background: #f0fdf4; color: #16a34a; border-color: #bbf7d0; }
  .role-finance  { background: #fefce8; color: #ca8a04; border-color: #fef08a; }
  .top-bar-user {
    font-size: 13px; color: #374151;
    display: flex; align-items: center; gap: 8px; font-weight: 500;
  }
  .top-bar-avatar {
    width: 30px; height: 30px; border-radius: 8px;
    background: linear-gradient(135deg, #1d4ed8, #3b82f6);
    display: flex; align-items: center; justify-content: center;
    font-size: 11px; font-weight: 800; color: #fff;
  }
  .logout-btn {
    display: flex; align-items: center; gap: 6px;
    padding: 6px 13px; background: #fff;
    border: 1.5px solid #e2e8f0; color: #64748b;
    border-radius: 7px; font-size: 12px; font-weight: 600;
    cursor: pointer; transition: all 0.14s;
    font-family: 'Inter', sans-serif;
  }
  .logout-btn:hover { background: #fef2f2; border-color: #fecaca; color: #dc2626; }

  .page-content { flex: 1; overflow: hidden; background: #f8fafc; }
`;

export default function App() {
    const [user,       setUser]       = useState(null);
    const [loginError, setLoginError] = useState('');
    const [loading,    setLoading]    = useState(false);
    const [showPass,   setShowPass]   = useState(false);

    const handleLogin = async (e) => {
        e.preventDefault();
        setLoginError('');
        setLoading(true);
        const email    = e.target.email.value.trim();
        const password = e.target.password.value;

        try {
            const data = await api('auth/login', 'POST', { username: email, password });
            if (data.success) {
                setUser({
                    id:            data.admin_id,
                    name:          data.full_name,
                    role:          (() => {
                        // Strictly use server-provided role string first
                        if (data.role === 'admin' || data.role === 'finance' || data.role === 'engineer') {
                            return data.role;
                        }
                        // Fallback: map access_level exactly
                        if (data.access_level === 0) return 'engineer';
                        if (data.access_level === 1) return 'admin';
                        if (data.access_level === 2) return 'finance';
                        return 'engineer'; // default safe fallback
                    })(),
                    access_level:  data.access_level,
                    engineer_site: data.engineer_site || null,
                });
            } else {
                setLoginError('Invalid credentials. Please check your email and password.');
            }
        } catch (err) {
            const msg = err.message || '';
            if (msg.includes('401') || msg.toLowerCase().includes('invalid')) {
                setLoginError('Invalid credentials. Please check your email and password.');
            } else if (msg.includes('Failed to fetch') || msg.includes('NetworkError')) {
                setLoginError('Cannot connect to server. Please check your internet connection.');
            } else {
                setLoginError('Login failed. Please try again.');
            }
        } finally {
            setLoading(false);
        }
    };

    // ── Login screen ──────────────────────────────────────────
    if (!user) {
        return (
            <>
                <style>{LOGIN_STYLES}</style>
                <div className="login-root">
                    {/* Left decorative panel */}
                    <div className="login-left">
                        <div className="login-left-content">
                            <div className="login-left-icon">
                                <Hammer size={36} color="#fff" />
                            </div>
                            <div className="login-left-title">
                                M R Constructions<br/>Management Portal
                            </div>
                            <div className="login-left-sub">
                                End-to-end visibility across all your<br />
                                sites, teams, materials and finances.
                            </div>
                            <div className="login-left-pills">
                                <div className="login-pill">Projects & Sites</div>
                                <div className="login-pill">Material Indents</div>
                                <div className="login-pill">DPR Reports</div>
                                <div className="login-pill">Issue Tracking</div>
                                <div className="login-pill">Labor Attendance</div>
                                <div className="login-pill">Inventory</div>
                            </div>
                        </div>
                    </div>

                    {/* Right form */}
                    <div className="login-right">
                        <div className="login-panel">
                            <div className="login-brand">
                                <div className="login-brand-icon">
                                    <Hammer size={20} color="#fff" />
                                </div>
                                <span className="login-brand-name">M R Constructions</span>
                                <span className="login-brand-sub">Construction Management Platform</span>
                            </div>

                            <div className="login-card">
                                <div className="login-card-title">Sign in to your account</div>
                                <div className="login-card-sub">Enter your credentials to continue</div>

                                {loginError && (
                                    <div className="login-error">
                                        <AlertCircle size={15} style={{ flexShrink: 0, marginTop: 1 }} />
                                        {loginError}
                                    </div>
                                )}

                                <form onSubmit={handleLogin}>
                                    <div className="login-field">
                                        <label>Email Address</label>
                                        <div className="login-input-wrap">
                                            <span className="fi">
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,12 2,6"/></svg>
                                            </span>
                                            <input
                                                name="email" type="email"
                                                placeholder="your@email.com"
                                                required autoComplete="email"
                                                className="login-input"
                                            />
                                        </div>
                                    </div>

                                    <div className="login-field">
                                        <label>Password</label>
                                        <div className="login-input-wrap">
                                            <span className="fi">
                                                <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
                                            </span>
                                            <input
                                                name="password"
                                                type={showPass ? 'text' : 'password'}
                                                placeholder="••••••••"
                                                required autoComplete="current-password"
                                                className="login-input"
                                            />
                                            <button
                                                type="button" className="eye-btn"
                                                onClick={() => setShowPass(s => !s)}
                                                tabIndex={-1}
                                            >
                                                {showPass ? <EyeOff size={15} /> : <Eye size={15} />}
                                            </button>
                                        </div>
                                    </div>

                                    <button type="submit" className="login-btn" disabled={loading}>
                                        {loading
                                            ? <><span className="spin">⟳</span> Signing in…</>
                                            : 'Sign In'
                                        }
                                    </button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </>
        );
    }

    // ── Authenticated shell ───────────────────────────────────
    return (
        <>
            <style>{LOGIN_STYLES}</style>
            <div className="app-root">
                <header className="top-bar">
                    <div className="top-bar-logo">
                        <div className="top-bar-logo-icon">
                            <Hammer size={15} color="#fff" />
                        </div>
                        MR CONSTRUCTIONS
                    </div>
                    <div className="top-bar-sep" />
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                        <span className={`top-bar-role ${user.role === 'admin' ? 'role-admin' : user.role === 'finance' ? 'role-finance' : 'role-engineer'}`}>
                            {user.role === 'admin' ? <Shield size={11} /> : user.role === 'finance' ? <Wallet size={11} /> : <HardHat size={11} />}
                            {user.role === 'admin' ? 'Admin' : user.role === 'finance' ? 'Finance' : 'Engineer'}
                        </span>
                        <div className="top-bar-user">
                            <div className="top-bar-avatar">
                                {user.name.split(' ').map(n => n[0]).join('').slice(0, 2)}
                            </div>
                            {user.name}
                        </div>
                        <button
                            className="logout-btn"
                            onClick={() => setUser(null)}
                        >
                            <LogOut size={13} /> Sign Out
                        </button>
                    </div>
                </header>

                <div className="page-content">
                    {user.role === 'admin'   && <AdminDashboard user={user} />}
                    {user.role === 'finance'  && <FinanceDashboard user={user} />}
                    {user.role === 'engineer' && <EngineerDashboard user={user} />}
                </div>
            </div>
        </>
    );
}